import type { LoginRequest, LoginResponse, RegisterRequest, User } from '@api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { authApi, getAccessToken, usersApi } from '@api';

// Query keys
export const userKeys = {
  all: ['user'] as const,
  me: () => [...userKeys.all, 'me'] as const,
  billing: () => [...userKeys.all, 'billing'] as const,
};

// Get current user - only when we have a token, so we avoid 401 on public pages
export const useCurrentUser = () => {
  return useQuery({
    queryKey: userKeys.me(),
    queryFn: () => usersApi.getMe(),
    enabled: !!getAccessToken(),
    retry: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

// Get billing address
export const useBillingAddress = () => {
  return useQuery({
    queryKey: userKeys.billing(),
    queryFn: () => usersApi.getBillingAddress(),
    retry: false,
  });
};

// Login mutation
export const useLogin = () => {
  const queryClient = useQueryClient();

  return useMutation<LoginResponse, Error, LoginRequest>({
    mutationFn: (data: LoginRequest) => authApi.login(data),
    onSuccess: (data) => {
      // Clear all cached queries so previous user's data is not shown
      queryClient.clear();
      queryClient.setQueryData(userKeys.me(), data.user);
    },
  });
};

// Register mutation
export const useRegister = () => {
  return useMutation({
    mutationFn: (data: RegisterRequest) => authApi.register(data),
  });
};

// Logout mutation
export const useLogout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => authApi.logout(),
    onSuccess: () => {
      queryClient.clear();
    },
  });
};

// Update profile mutation
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<Pick<User, 'first_name' | 'last_name' | 'company_name'>>) =>
      usersApi.updateMe(data),
    onSuccess: (data) => {
      queryClient.setQueryData(userKeys.me(), data);
    },
  });
};

// Update billing address mutation
export const useUpdateBillingAddress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Parameters<typeof usersApi.updateBillingAddress>[0]) =>
      usersApi.updateBillingAddress(data),
    onSuccess: (data) => {
      queryClient.setQueryData(userKeys.billing(), data);
    },
  });
};

// Password reset mutations
export const useRequestPasswordReset = () => {
  return useMutation({
    mutationFn: (email: string) => authApi.requestPasswordReset(email),
  });
};

export const useConfirmPasswordReset = () => {
  return useMutation({
    mutationFn: (data: Parameters<typeof authApi.confirmPasswordReset>[0]) =>
      authApi.confirmPasswordReset(data),
  });
};
